import { useEffect, useState } from "react";
import { getAuthCookie } from "../utils/authCookies";
import {
  allActionsAllowed,
  fetchModuleActionPermissions,
  isTeamMemberUser,
  noActionsAllowed,
  actionsToFlags,
  type ModuleActionFlags,
  type ModulePermissionName,
} from "../utils/moduleActionPermissions";

export type UseModuleActionPermissionsResult = ModuleActionFlags & {
  /** True while fetching for team members. Firm admins resolve immediately. */
  loading: boolean;
  /** True when CRUD buttons must follow API permissions (team members). */
  enforce: boolean;
  error: string | null;
};

/**
 * Listing-page CRUD visibility for signage / brokerage team members.
 *
 * - Firm admins (non–team-member): all actions allowed, no API call.
 * - Team members: GET /permission-scope/module-permissions and map create/view/edit/delete.
 *
 * Usage:
 *   const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions("projects");
 *   {canCreate && <button>+ Create</button>}
 *   {canView && <ViewIconButton ... />}
 */
export function useModuleActionPermissions(
  moduleName: ModulePermissionName
): UseModuleActionPermissionsResult {
  const enforce = isTeamMemberUser();
  const [flags, setFlags] = useState<ModuleActionFlags>(() =>
    enforce ? noActionsAllowed() : allActionsAllowed()
  );
  const [loading, setLoading] = useState<boolean>(enforce);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enforce) {
      setFlags(allActionsAllowed());
      setLoading(false);
      setError(null);
      return;
    }

    const module = String(moduleName || "").trim();
    if (!module) {
      setFlags(noActionsAllowed());
      setLoading(false);
      setError("Missing module name");
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    setLoading(true);
    setError(null);
    setFlags(noActionsAllowed());

    (async () => {
      try {
        const actions = await fetchModuleActionPermissions(module, {
          userId: getAuthCookie("userId") || undefined,
          token: getAuthCookie("sessionToken") || undefined,
          signal: controller.signal,
        });
        if (cancelled) return;
        setFlags(actionsToFlags(actions));
      } catch (err) {
        if (cancelled || (err as { name?: string })?.name === "AbortError") return;
        setFlags(noActionsAllowed());
        setError(err instanceof Error ? err.message : "Failed to load permissions");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [enforce, moduleName]);

  return {
    ...flags,
    loading,
    enforce,
    error,
  };
}

export default useModuleActionPermissions;
